import { Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { faChevronRight, faClockRotateLeft, faFileExcel, faSearch, faTag, faUser } from '@fortawesome/free-solid-svg-icons';
import { ColumnModel, FilterSettingsModel, GridComponent, PageSettingsModel, RowSelectEventArgs } from '@syncfusion/ej2-angular-grids';
import * as moment from 'moment';
import { ErrorService } from '../../../../../@core/utils/error.service';
import { TransactionService } from '../../../../core/services/TransactionService';
import { KwFilterDropdownService } from '../../../../core/widgets/kw-filter-dropdown/kw-filter-dropdown.service';
import { FilterSettings } from '../../../../core/widgets/kw-filter-dropdown/models/filter-options';
import { FilterResults } from '../../../../core/widgets/kw-filter-dropdown/models/filter-results';
import { TransactionDetailDialogComponent } from './transaction-detail-dialog/transaction-detail-dialog.component';
import { toTransactionRow, TransactionRow } from './transaction-row';

const MOBILE_PAGE_SIZE = 25;
const ALL_TYPES = 'All';

interface TypeChip {
  label: string;
  count: number;
}

@Component({
  selector: 'transaction-table',
  templateUrl: './transaction-table.component.html',
  styleUrls: ['./transaction-table.component.scss']
})
export class TransactionTableComponent implements OnInit {
  @ViewChild('dateTemplate', { static: true }) public dateTemplate: any;
  @ViewChild('typeTemplate', { static: true }) public typeTemplate: any;
  @ViewChild('changeTemplate', { static: true }) public changeTemplate: any;
  @ViewChild('actionTemplate', { static: true }) public actionTemplate: any;
  @ViewChild('grid', { static: false }) grid: GridComponent;

  public faChevronRight = faChevronRight;
  public faClockRotateLeft = faClockRotateLeft;
  public faFileExcel = faFileExcel;
  public faSearch = faSearch;
  public faTag = faTag;
  public faUser = faUser;

  public readonly allTypes = ALL_TYPES;
  public isLoading = true;
  private initialLoad = true;
  public searchTerm = '';
  public typeFilter = ALL_TYPES;
  public typeChips: TypeChip[] = [];
  public rows: TransactionRow[] = [];
  public allRows: TransactionRow[] = [];
  public columns: ColumnModel[] = [];
  public mobileVisibleCount = MOBILE_PAGE_SIZE;

  public pageSettings: PageSettingsModel = { pageSize: 50, pageSizes: [50, 100, 200] };
  public filterSettings: FilterSettingsModel = { mode: 'Immediate', type: 'Excel' };

  public dropdownSettings: FilterSettings = {
    dateRangeSettings: {
      display: true,
      singleSelection: true,
      startDate: moment().startOf('day').subtract(7, 'd').toDate(),
      endDate: moment().endOf('day').toDate()
    }
  };

  private destroyRef = inject(DestroyRef);

  constructor(
    private dialog: MatDialog,
    private errorService: ErrorService,
    private transactionService: TransactionService,
    public filterService: KwFilterDropdownService,
  ) { }

  ngOnInit() {
    this.setColumns();
  }

  // The date-range widget emits on init, which triggers the first load.
  setFilterResults(results: FilterResults) {
    if (this.initialLoad) {
      this.initialLoad = false;
      results = this.filterService.getFilterState();
    }

    const range = this.dropdownSettings.dateRangeSettings;
    const [startDate, endDate] = results?.selectedDateRange ?? [];
    range.startDate = startDate ?? range.startDate;
    range.endDate = endDate ?? range.endDate;
    this.getData();
  }

  getData() {
    const { startDate, endDate } = this.dropdownSettings.dateRangeSettings;
    this.isLoading = true;
    this.transactionService.getTransactions(startDate, endDate).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: transactions => {
        this.allRows = (transactions ?? [])
          .map(toTransactionRow)
          .sort((a, b) => (b.createdDate?.getTime() ?? 0) - (a.createdDate?.getTime() ?? 0));
        this.typeChips = this.countTypes(this.allRows);
        if (this.typeFilter !== ALL_TYPES && !this.typeChips.some(chip => chip.label === this.typeFilter))
          this.typeFilter = ALL_TYPES;
        this.applyFilters();
        this.isLoading = false;
      }, error: err => {
        this.errorService.handleError(err);
        this.isLoading = false;
      }
    });
  }

  setTypeFilter(type: string) {
    this.typeFilter = type;
    this.applyFilters();
  }

  applyFilters() {
    const term = this.searchTerm.trim().toLowerCase();
    this.rows = this.allRows.filter(row =>
      (this.typeFilter === ALL_TYPES || row.typeLabel === this.typeFilter) &&
      (!term || [row.barcode, row.products, row.lots, row.reason, row.details, row.user]
        .some(value => value.toLowerCase().includes(term))));
    this.mobileVisibleCount = MOBILE_PAGE_SIZE;
  }

  onRowSelected(args: RowSelectEventArgs) {
    const row = args?.data as TransactionRow;
    if (row) this.openDetail(row);
  }

  openDetail(row: TransactionRow) {
    TransactionDetailDialogComponent.open(this.dialog, row)
      .afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.grid?.clearSelection());
  }

  // Excel's checkbox filter stops at 1000 distinct values by default.
  actionBegin(args: { requestType?: string; filterChoiceCount?: number }) {
    if (args.requestType === 'filterchoicerequest' || args.requestType === 'filtersearchbegin')
      args.filterChoiceCount = 500000;
  }

  exportExcel() {
    this.grid?.excelExport({ includeHiddenColumn: true });
  }

  showMoreMobile() {
    this.mobileVisibleCount += MOBILE_PAGE_SIZE;
  }

  trackByRow(_: number, row: TransactionRow): number | undefined {
    return row.id;
  }

  private countTypes(rows: TransactionRow[]): TypeChip[] {
    const counts = new Map<string, number>();
    rows.forEach(row => counts.set(row.typeLabel, (counts.get(row.typeLabel) ?? 0) + 1));
    return [...counts].map(([label, count]) => ({ label, count })).sort((a, b) => b.count - a.count);
  }

  private setColumns() {
    this.columns = [
      { field: 'createdDate', headerText: 'Date', type: 'date', format: { type: 'dateTime', format: 'MM/dd/yyyy h:mm a' }, width: 130, template: this.dateTemplate },
      { field: 'typeLabel', headerText: 'Type', width: 130, template: this.typeTemplate },
      { field: 'barcode', headerText: 'Barcode', width: 110 },
      { field: 'products', headerText: 'Product', width: 180, clipMode: 'EllipsisWithTooltip' },
      { field: 'lots', headerText: 'Lot', width: 130, clipMode: 'EllipsisWithTooltip' },
      { field: 'changeText', headerText: 'Change', width: 200, template: this.changeTemplate, clipMode: 'EllipsisWithTooltip' },
      { field: 'originalQty', headerText: 'Original Qty', visible: false },
      { field: 'adjustedQty', headerText: 'New Qty', visible: false },
      { field: 'reason', headerText: 'Reason', width: 120, clipMode: 'EllipsisWithTooltip' },
      { field: 'details', headerText: 'Details', width: 260, clipMode: 'EllipsisWithTooltip' },
      { field: 'user', headerText: 'User', width: 140, clipMode: 'EllipsisWithTooltip' },
      { headerText: '', template: this.actionTemplate, width: 48, allowSorting: false, allowFiltering: false, textAlign: 'Center' }
    ];
  }
}
